import type { RestApiDayAvailabilityDto, RestApiOutageDto } from '@order-mage/integration-sdk';
import dayjs, { type Dayjs } from 'dayjs';
import type { LocalizedString } from '@/shared/model/Language';
import type { FormattedVenueOpeningHour } from './get-formatted-venue-opening-hours.util';

export enum VenueAvailabilityKind {
    Open = 'open',
    Closed = 'closed',
    ClosedUnknown = 'closed_unknown',
    Outage = 'outage',
}

export type OutageWithTimeAvailability = RestApiOutageDto & {
    timeAvailability: RestApiDayAvailabilityDto[] | null;
};

/** Minimal outage shape accepted by status / filter helpers (tests + DTO slices). */
export type VenueAvailabilityOutageInput = {
    endsAt?: string | null;
    description?: LocalizedString | null;
    timeAvailability?: Array<{
        availableIntervals?: Array<{ start: string; end: string }> | null;
    }> | null;
};

/** How UI should phrase “back at …” for an active outage (thresholds: ≤0 / ≤60 min / ≤24 h). */
export enum OutageReturnMode {
    Soon = 'soon',
    Minutes = 'minutes',
    AtTime = 'at_time',
    UntilDate = 'until_date',
}

export enum OutageReturnDay {
    Today = 'today',
    Tomorrow = 'tomorrow',
}

export type OutageReturn =
    | { mode: OutageReturnMode.Soon }
    | { mode: OutageReturnMode.Minutes; minutesUntil: number }
    | { mode: OutageReturnMode.AtTime; day: OutageReturnDay }
    | { mode: OutageReturnMode.UntilDate };

/** Pure venue open / closed / outage state for navbar, card, and cart. */
export type VenueAvailabilityStatus =
    | {
          kind: VenueAvailabilityKind.Open;
          /** 0 = today, 1 = tomorrow */
          closesAtDayDiff: number;
          closesAtFormatted: string;
          closesAtMs: number;
      }
    | {
          kind: VenueAvailabilityKind.Closed;
          /** 0 = today, 1 = tomorrow, 2+ = later */
          dayDiff: number;
          opensAtFormatted: string;
          opensAtDayjs: Dayjs;
      }
    | { kind: VenueAvailabilityKind.ClosedUnknown }
    | {
          kind: VenueAvailabilityKind.Outage;
          endsAt: string;
          endsAtDayjs: Dayjs;
          outageReturn: OutageReturn;
          description: LocalizedString | null | undefined;
      };

export const EMPTY_VENUE_AVAILABILITY_STATUS: VenueAvailabilityStatus = {
    kind: VenueAvailabilityKind.ClosedUnknown,
};

export type VenueAvailabilityComputedSnapshot = {
    computedAtMs: number;
    currentOutages: OutageWithTimeAvailability[];
    allOutages: OutageWithTimeAvailability[];
    status: VenueAvailabilityStatus;
    nextRecomputeAtMs: number | null;
    formattedOpeningHours: FormattedVenueOpeningHour[];
};

type IntervalEndMode = 'inclusive' | 'exclusive';

/** Interval membership with explicit end inclusivity. */
const isInInterval = (at: Dayjs, start: Dayjs, end: Dayjs, endMode: IntervalEndMode): boolean => {
    if (!start.isValid() || !end.isValid()) {
        return false;
    }
    if (at.isBefore(start)) {
        return false;
    }
    if (endMode === 'inclusive') {
        return at.isBefore(end) || at.isSame(end);
    }
    return at.isBefore(end);
};

/** True if `at` falls inside any absolute opening-hour interval (start inclusive, end exclusive). */
const isWithinOpeningHours = (at: Dayjs, openingHours: FormattedVenueOpeningHour[]): boolean => {
    for (const day of openingHours) {
        for (const abs of day.absoluteIntervals) {
            if (isInInterval(at, dayjs(abs.start), dayjs(abs.end), 'exclusive')) {
                return true;
            }
        }
    }
    return false;
};

/** Adjacent / touching slices only — a multi-hour open gap is a separate outage window. */
const CONTIGUOUS_GAP_MS = 2 * 60 * 1000;

type OutageEnd = {
    date: Dayjs;
    iso: string;
    description: LocalizedString | null | undefined;
};

/**
 * End of the contiguous outage spell that contains `now` (merge touching / tiny-gap slices).
 * Ignores later disconnected schedule windows (e.g. tomorrow’s separate evening slot).
 */
const getContiguousOutageRunEnd = (
    intervals: Array<{ start: string; end: string }>,
    now: Dayjs,
): { date: Dayjs; iso: string } | null => {
    const parsed = intervals
        .map((interval) => ({
            start: dayjs(interval.start),
            end: dayjs(interval.end),
            iso: interval.end,
        }))
        .filter((i) => i.start.isValid() && i.end.isValid())
        .sort((a, b) => (a.start.isBefore(b.start) ? -1 : 1));

    const startIdx = parsed.findIndex((i) => isInInterval(now, i.start, i.end, 'inclusive'));
    if (startIdx < 0) {
        return null;
    }

    let runEnd = parsed[startIdx].end;
    let runIso = parsed[startIdx].iso;
    for (let i = startIdx + 1; i < parsed.length; i++) {
        const next = parsed[i];
        if (next.start.diff(runEnd) > CONTIGUOUS_GAP_MS) {
            break;
        }
        if (next.end.isAfter(runEnd)) {
            runEnd = next.end;
            runIso = next.iso;
        }
    }
    if (!runEnd.isAfter(now)) {
        return null;
    }
    return { date: runEnd, iso: runIso };
};

const getOutageIntervals = (outage: VenueAvailabilityOutageInput) =>
    (outage.timeAvailability ?? []).flatMap((day) => day.availableIntervals ?? []);

/**
 * Future end times from active outages.
 * Intervals win when present (TIME_PLAN windows); `endsAt` caps that run or stands alone.
 */
const getOutageEnds = (outages: VenueAvailabilityOutageInput[], now: Dayjs): OutageEnd[] => {
    const endTimes: OutageEnd[] = [];
    for (const outage of outages) {
        const intervals = getOutageIntervals(outage);
        if (intervals.length > 0) {
            const runEnd = getContiguousOutageRunEnd(intervals, now);
            if (!runEnd) {
                continue;
            }
            let date = runEnd.date;
            let iso = runEnd.iso;
            if (outage.endsAt) {
                const endsAt = dayjs(outage.endsAt);
                if (endsAt.isValid() && endsAt.isBefore(date)) {
                    date = endsAt;
                    iso = outage.endsAt;
                }
            }
            if (date.isAfter(now)) {
                endTimes.push({ date, iso, description: outage.description });
            }
            continue;
        }

        if (outage.endsAt) {
            const endsAt = dayjs(outage.endsAt);
            if (endsAt.isValid() && endsAt.isAfter(now)) {
                endTimes.push({
                    date: endsAt,
                    iso: outage.endsAt,
                    description: outage.description,
                });
            }
        }
    }
    return endTimes;
};

/** Maps remaining time until `endsAt` → outage return mode (soon / minutes / clock / date). */
const getOutageReturn = (endsAt: Dayjs, now: Dayjs): OutageReturn => {
    const ms = endsAt.diff(now);
    const minutes = Math.floor(ms / 60_000);
    if (minutes <= 0) {
        return { mode: OutageReturnMode.Soon };
    }
    if (minutes <= 60) {
        return { mode: OutageReturnMode.Minutes, minutesUntil: minutes };
    }
    const dayDiff = endsAt.startOf('day').diff(now.startOf('day'), 'day');
    // Today / tomorrow always use clock copy (“today/tomorrow at HH:mm”), even when >24h away.
    if (dayDiff <= 1) {
        return {
            mode: OutageReturnMode.AtTime,
            day: dayDiff >= 1 ? OutageReturnDay.Tomorrow : OutageReturnDay.Today,
        };
    }
    return { mode: OutageReturnMode.UntilDate };
};

/** Earliest absolute interval start strictly after `now`, with display HH:mm. */
const getNextOpening = (
    openingHours: FormattedVenueOpeningHour[],
    now: Dayjs,
): { start: Dayjs; formattedStart: string } | undefined => {
    const all: Array<{ start: Dayjs; formattedStart: string }> = [];
    for (const day of openingHours) {
        day.absoluteIntervals.forEach((abs, idx) => {
            all.push({
                start: dayjs(abs.start),
                formattedStart: day.intervals[idx]?.start ?? dayjs(abs.start).format('HH:mm'),
            });
        });
    }
    all.sort((a, b) => (a.start.isBefore(b.start) ? -1 : 1));
    return all.find((i) => i.start.isAfter(now));
};

/** Current absolute interval containing `now` (end exclusive), with display end HH:mm. */
const getCurrentClosing = (
    openingHours: FormattedVenueOpeningHour[],
    now: Dayjs,
): { end: Dayjs; formattedEnd: string } | undefined => {
    for (const day of openingHours) {
        for (let idx = 0; idx < day.absoluteIntervals.length; idx++) {
            const abs = day.absoluteIntervals[idx];
            const start = dayjs(abs.start);
            const end = dayjs(abs.end);
            if (isInInterval(now, start, end, 'exclusive')) {
                return {
                    end,
                    formattedEnd: day.intervals[idx]?.end ?? end.format('HH:mm'),
                };
            }
        }
    }
    return undefined;
};

/** `kind: closed` including dayDiff vs today for opens-today/tomorrow/later copy. */
const getClosedFromNextOpen = (
    next: { start: Dayjs; formattedStart: string },
    now: Dayjs,
): VenueAvailabilityStatus => ({
    kind: VenueAvailabilityKind.Closed,
    dayDiff: next.start.startOf('day').diff(now.startOf('day'), 'day'),
    opensAtFormatted: next.formattedStart,
    opensAtDayjs: next.start,
});

const getOpenFromClosing = (
    closing: { end: Dayjs; formattedEnd: string },
    now: Dayjs,
): VenueAvailabilityStatus => ({
    kind: VenueAvailabilityKind.Open,
    closesAtDayDiff: closing.end.startOf('day').diff(now.startOf('day'), 'day'),
    closesAtFormatted: closing.formattedEnd,
    closesAtMs: closing.end.valueOf(),
});

const getOutageStatus = (latest: OutageEnd, now: Dayjs): VenueAvailabilityStatus => ({
    kind: VenueAvailabilityKind.Outage,
    endsAt: latest.iso,
    endsAtDayjs: latest.date,
    outageReturn: getOutageReturn(latest.date, now),
    description: latest.description,
});

const areOutageReturnsEqual = (a: OutageReturn, b: OutageReturn): boolean => {
    if (a.mode !== b.mode) {
        return false;
    }
    switch (a.mode) {
        case OutageReturnMode.Soon:
            return true;
        case OutageReturnMode.Minutes:
            return (
                a.minutesUntil ===
                (b as Extract<OutageReturn, { mode: OutageReturnMode.Minutes }>).minutesUntil
            );
        case OutageReturnMode.AtTime: {
            const bb = b as Extract<OutageReturn, { mode: OutageReturnMode.AtTime }>;
            return a.day === bb.day;
        }
        case OutageReturnMode.UntilDate:
            return true;
    }
};

/** Semantic equality for store identity reuse (Zustand selector Object.is). */
export const areVenueAvailabilityStatusesEqual = (
    a: VenueAvailabilityStatus,
    b: VenueAvailabilityStatus,
): boolean => {
    if (a.kind !== b.kind) {
        return false;
    }
    switch (a.kind) {
        case VenueAvailabilityKind.Open: {
            const bb = b as Extract<VenueAvailabilityStatus, { kind: VenueAvailabilityKind.Open }>;
            return (
                a.closesAtMs === bb.closesAtMs &&
                a.closesAtFormatted === bb.closesAtFormatted &&
                a.closesAtDayDiff === bb.closesAtDayDiff
            );
        }
        case VenueAvailabilityKind.Closed: {
            const bb = b as Extract<VenueAvailabilityStatus, { kind: VenueAvailabilityKind.Closed }>;
            return (
                a.dayDiff === bb.dayDiff &&
                a.opensAtFormatted === bb.opensAtFormatted &&
                a.opensAtDayjs.valueOf() === bb.opensAtDayjs.valueOf()
            );
        }
        case VenueAvailabilityKind.ClosedUnknown:
            return true;
        case VenueAvailabilityKind.Outage: {
            const bb = b as Extract<VenueAvailabilityStatus, { kind: VenueAvailabilityKind.Outage }>;
            return (
                a.endsAt === bb.endsAt &&
                a.endsAtDayjs.valueOf() === bb.endsAtDayjs.valueOf() &&
                areOutageReturnsEqual(a.outageReturn, bb.outageReturn) &&
                a.description === bb.description
            );
        }
    }
};

/** Same active outage membership (by id + endsAt) — reuse array ref when unchanged. */
export const areCurrentOutagesEqual = (
    a: OutageWithTimeAvailability[],
    b: OutageWithTimeAvailability[],
): boolean => {
    if (a === b) {
        return true;
    }
    if (a.length !== b.length) {
        return false;
    }
    return a.every((outage, i) => outage.id === b[i].id && outage.endsAt === b[i].endsAt);
};

/**
 * Pure availability for UI: open, closed (+ when), closed_unknown, or outage (+ return phrasing).
 * Priority: active outage (if end is within OH) → else next regular open. No i18n / no JSX.
 */
export const getVenueAvailabilityStatus = ({
    openingHours,
    outages,
    now: nowInput,
}: {
    openingHours: FormattedVenueOpeningHour[];
    outages: VenueAvailabilityOutageInput[];
    now?: Dayjs;
}): VenueAvailabilityStatus => {
    const now = nowInput ?? dayjs();

    if (outages.length > 0) {
        const ends = getOutageEnds(outages, now);
        if (ends.length > 0) {
            const latest = ends.reduce((a, b) => (b.date.isAfter(a.date) ? b : a));
            if (isWithinOpeningHours(latest.date, openingHours) || openingHours.length === 0) {
                return getOutageStatus(latest, now);
            }
            const nextWhileOutage = getNextOpening(openingHours, latest.date);
            if (nextWhileOutage) {
                return getClosedFromNextOpen(nextWhileOutage, now);
            }
            return getOutageStatus(latest, now);
        }
    }

    const closing = getCurrentClosing(openingHours, now);
    if (closing) {
        return getOpenFromClosing(closing, now);
    }

    const next = getNextOpening(openingHours, now);
    if (!next) {
        return { kind: VenueAvailabilityKind.ClosedUnknown };
    }
    return getClosedFromNextOpen(next, now);
};

/** Outages still active at `computedAtMs` (inside a TIME_PLAN window, or endsAt-only still open). */
export const getCurrentOutages = (
    outages: OutageWithTimeAvailability[],
    computedAtMs: number,
): OutageWithTimeAvailability[] => {
    if (!outages.length) {
        return [];
    }
    const at = dayjs(computedAtMs);
    return outages.filter((outage) => {
        const intervals = getOutageIntervals(outage);
        if (intervals.length > 0) {
            return intervals.some((interval) => {
                const start = dayjs(interval.start);
                const end = dayjs(interval.end);
                return isInInterval(at, start, end, 'inclusive');
            });
        }
        if (outage.endsAt != null) {
            const endsAt = dayjs(outage.endsAt);
            return endsAt.isValid() && endsAt.isAfter(at);
        }
        return false;
    });
};

const ONE_MINUTE_MS = 60_000;

/** Earliest future ms when status copy may change (countdown tick / T−60 / opensAt / closesAt). */
const getNextStatusRecomputeAtMs = (status: VenueAvailabilityStatus, computedAtMs: number): number | null => {
    const candidates: number[] = [];
    if (status.kind === VenueAvailabilityKind.Outage) {
        candidates.push(status.endsAtDayjs.valueOf());
        if (status.outageReturn.mode === OutageReturnMode.Minutes) {
            candidates.push(computedAtMs + ONE_MINUTE_MS);
        }
        if (status.outageReturn.mode === OutageReturnMode.AtTime) {
            candidates.push(status.endsAtDayjs.valueOf() - 60 * ONE_MINUTE_MS);
        }
    } else if (status.kind === VenueAvailabilityKind.Closed) {
        candidates.push(status.opensAtDayjs.valueOf());
    } else if (status.kind === VenueAvailabilityKind.Open) {
        candidates.push(status.closesAtMs);
    }
    const future = candidates.filter((t) => t > computedAtMs);
    return future.length ? Math.min(...future) : null;
};

/** Earliest future ms when the availability snapshot should be recomputed (outage edges + status copy). */
export const getNextVenueAvailabilityRecomputeAtMs = ({
    currentOutages,
    allOutages,
    computedAtMs,
    status,
}: {
    currentOutages: OutageWithTimeAvailability[];
    allOutages: OutageWithTimeAvailability[];
    computedAtMs: number;
    status: VenueAvailabilityStatus;
}): number | null => {
    const at = dayjs(computedAtMs);
    const candidates: number[] = [];

    for (const outage of currentOutages) {
        if (outage.endsAt) {
            const endsAt = dayjs(outage.endsAt);
            if (endsAt.isValid() && endsAt.isAfter(at)) {
                candidates.push(endsAt.valueOf());
            }
        }
        for (const day of outage.timeAvailability ?? []) {
            for (const interval of day.availableIntervals ?? []) {
                const start = dayjs(interval.start);
                const end = dayjs(interval.end);
                if (isInInterval(at, start, end, 'inclusive') && end.isAfter(at)) {
                    candidates.push(end.valueOf());
                }
            }
        }
    }

    for (const outage of allOutages) {
        for (const day of outage.timeAvailability ?? []) {
            for (const interval of day.availableIntervals ?? []) {
                const start = dayjs(interval.start);
                if (start.isValid() && start.isAfter(at)) {
                    candidates.push(start.valueOf());
                }
            }
        }
    }

    const statusRecomputeAtMs = getNextStatusRecomputeAtMs(status, computedAtMs);
    if (statusRecomputeAtMs != null) {
        candidates.push(statusRecomputeAtMs);
    }

    const future = candidates.filter((t) => t > computedAtMs);
    return future.length ? Math.min(...future) : null;
};

/**
 * True when a timed-order slot is unavailable due to an outage.
 * TIME_PLAN intervals win when present; otherwise `endsAt`. Lead time extends past the window end.
 */
export const isTimedSlotInOutage = (
    slotStart: Dayjs,
    slotEnd: Dayjs,
    outages: VenueAvailabilityOutageInput[],
    minimumMinutesAfterOutage = 0,
): boolean => {
    const lead = Math.max(0, minimumMinutesAfterOutage);
    return outages.some((outage) => {
        const intervals = getOutageIntervals(outage);
        if (intervals.length > 0) {
            return intervals.some((interval) => {
                const intStart = dayjs(interval.start);
                const intEnd = dayjs(interval.end);
                return slotStart.isBefore(intEnd.add(lead, 'minutes')) && slotEnd.isAfter(intStart);
            });
        }
        if (outage.endsAt) {
            const outageEnd = dayjs(outage.endsAt);
            return outageEnd.isValid() && slotStart.isBefore(outageEnd.add(lead, 'minutes'));
        }
        return false;
    });
};

/** One-shot compute for the store: status + outages + next timer edge. */
export const computeVenueAvailabilitySnapshot = ({
    computedAtMs,
    outages,
    formattedOpeningHours,
}: {
    computedAtMs: number;
    outages: OutageWithTimeAvailability[] | undefined;
    formattedOpeningHours: FormattedVenueOpeningHour[];
}): VenueAvailabilityComputedSnapshot => {
    const allOutages = outages ?? [];
    const currentOutages = getCurrentOutages(allOutages, computedAtMs);
    const status = getVenueAvailabilityStatus({
        openingHours: formattedOpeningHours,
        outages: currentOutages,
        now: dayjs(computedAtMs),
    });
    const nextRecomputeAtMs = getNextVenueAvailabilityRecomputeAtMs({
        currentOutages,
        allOutages,
        computedAtMs,
        status,
    });
    return {
        computedAtMs,
        currentOutages,
        allOutages,
        status,
        nextRecomputeAtMs,
        formattedOpeningHours,
    };
};
